import React from "react";
import type { ColumnsType } from "antd/es/table";
import { Localize } from "components/service";
import CommunicationFields from "../fields";
import { formatCommunicationDateTime, formatRelativeCommunicationDate } from "./communication-date-helper";
import type {
  TCommunicationAuditEntry,
  TCommunicationReferenceOption,
  TCommunicationTab,
  TCommunicationTableLayout,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "./communication-types-helper";

interface ICreateCommunicationColumnsProps {
  currentTab: TCommunicationTab;
  layoutMode: TCommunicationTableLayout;
  canEditRaw: boolean;
  canApprove: boolean;
  equipmentOptions: TCommunicationReferenceOption[];
  flooringOptions: TCommunicationReferenceOption[];
  organizerOptions: TCommunicationReferenceOption[];
  exhibitionOptions: TCommunicationReferenceOption[];
  auditEntryMap: Record<string, TCommunicationAuditEntry | undefined>;
  onSave: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onApprove: (taskId: string, values: TCommunicationTaskFormValues) => void;
}

const formatStandNumberLabel = (value?: string | null): string => {
  const normalizedValue = typeof value === "string" ? value.trim() : "";

  if (!normalizedValue) {
    return "\u2014";
  }

  const withoutPrefix = normalizedValue.replace(/^(\u2116\s*|N\s*|#\s*)/iu, "").trim();

  return `\u2116 ${withoutPrefix || normalizedValue}`;
};

const getStandNumberLabels = (task: TCommunicationTask, fallbackValue?: string | null): string[] => {
  const standLabels = Array.from(
    new Set(
      task.request_groups
        .map((group) => formatStandNumberLabel(group.stand_number))
        .filter((label) => label && label !== "\u2014")
    )
  );

  if (standLabels.length > 0) {
    return standLabels;
  }

  return [ formatStandNumberLabel(fallbackValue) ];
};

const baseColumns = (layoutMode: TCommunicationTableLayout): ColumnsType<TCommunicationTask> => {
  const isCompactLayout = layoutMode !== "full";
  const isTabletLayout = layoutMode === "tablet";
  const columns: ColumnsType<TCommunicationTask> = [
    {
      title: <Localize>COMMUNICATION.Table_Email</Localize>,
      dataIndex: "sender",
      key: "sender",
      width: isTabletLayout ? 248 : 276,
      sorter: true,
      render: (_, task) => <CommunicationFields.Info task={task} />,
    },
    {
      title: <Localize>COMMUNICATION.Table_EventCompany</Localize>,
      dataIndex: "event_name",
      key: "event_name",
      width: isTabletLayout ? 212 : 228,
      sorter: true,
      render: (_, task) => <CommunicationFields.Overview task={task} />,
    },
    {
      title: <Localize>COMMUNICATION.Table_Stand</Localize>,
      dataIndex: "stand_number",
      key: "stand_number",
      width: isTabletLayout ? 90 : 102,
      sorter: true,
      render: (value: string, task) => {
        const standLabels = getStandNumberLabels(task, value);
        const visibleStandLabels = standLabels.slice(0, 2);
        const hiddenStandsCount = Math.max(0, standLabels.length - visibleStandLabels.length);
        const standCountKey =
          standLabels.length === 1
            ? "COMMUNICATION.Info_StandGroupsCount"
            : "COMMUNICATION.Info_StandGroupsCountPlural";

        return (
          <div className="communication-table__stand-cell" title={standLabels.join(" • ")}>
            <div className="communication-table__stand-list">
              {visibleStandLabels.map((standLabel) => (
                <span
                  key={standLabel}
                  className="communication-table__stand-value"
                >
                  {standLabel}
                </span>
              ))}
              {hiddenStandsCount > 0 && (
                <span className="communication-table__stand-more">{`+${hiddenStandsCount}`}</span>
              )}
            </div>

            {standLabels.length > 1 && (
              <span className="communication-table__stand-badge">
                <Localize count={standLabels.length}>{standCountKey}</Localize>
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: <Localize>COMMUNICATION.Table_Changes</Localize>,
      dataIndex: "final_changes",
      key: "final_changes",
      width: isTabletLayout ? 200 : isCompactLayout ? 220 : 240,
      render: (_, task) => <CommunicationFields.Changes task={task} />,
    },
  ];

  if (!isCompactLayout) {
    columns.push({
      title: <Localize>COMMUNICATION.Table_Questions</Localize>,
      dataIndex: "questions",
      key: "questions",
      width: 156,
      sorter: true,
      render: (_, task) => <CommunicationFields.Questions task={task} />,
    });
  }

  return columns;
};

const operationalColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> => {
  const isCompactLayout = props.layoutMode !== "full";
  const isTabletLayout = props.layoutMode === "tablet";
  const columns: ColumnsType<TCommunicationTask> = [ ...baseColumns(props.layoutMode) ];

  if (!isCompactLayout) {
    columns.push({
      title: <Localize>COMMUNICATION.Table_NextStep</Localize>,
      dataIndex: "status",
      key: "status",
      width: 148,
      sorter: true,
      render: (_, task) => <CommunicationFields.NextStep task={task} />,
    });
  }

  columns.push(
    {
      title: <Localize>COMMUNICATION.Table_Received</Localize>,
      dataIndex: "received_at",
      key: "received_at",
      width: isTabletLayout ? 134 : 146,
      sorter: true,
      render: (value: string) => (
        <span className="communication-table__date-value communication-table__date-value--relative">
          <span className="communication-table__date-relative">{formatRelativeCommunicationDate(value) || "\u2014"}</span>
          <span className="communication-table__date-full">{formatCommunicationDateTime(value)}</span>
        </span>
      ),
    }
  );

  return columns;
};

const auditColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> => {
  const isCompactLayout = props.layoutMode !== "full";
  const isTabletLayout = props.layoutMode === "tablet";
  const columns: ColumnsType<TCommunicationTask> = [ ...baseColumns(props.layoutMode) ];

  if (!isCompactLayout) {
    columns.push({
      title: <Localize>COMMUNICATION.Table_ProcessedBy</Localize>,
      key: "processed_by",
      width: 156,
      sorter: true,
      render: (_, task) => (
        <span className="communication-table__user-value">{task.dismissed_by || task.approved_by || "\u2014"}</span>
      ),
    });
  }

  columns.push(
    {
      title: <Localize>COMMUNICATION.Table_ProcessedAt</Localize>,
      key: "processed_at",
      width: isTabletLayout ? 138 : 148,
      sorter: true,
      render: (_, task) => (
        <span className="communication-table__date-value">
          {task.dismissed_at || task.approved_at ? formatCommunicationDateTime(task.dismissed_at || task.approved_at) : "\u2014"}
        </span>
      ),
    }
  );

  return columns;
};

export const createCommunicationColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> =>
  props.currentTab === "audit" ? auditColumns(props) : operationalColumns(props);
